<template>
  <DSFieldset
    ref="fieldsetRef"
    :label="props.label"
    :direction="props.direction"
    :required="props.required"
    :disabled="props.disabled"
    :tooltip="props.tooltip"
    :hidden-label="props.hiddenLabel"
    :with-divider="false"
    :error-messages="dsFormField.errorMessages.value"
    :name="props.name"
    :class="{ 'is-error': hasErrors }"
  >
    <DSRadioButton
      v-for="option in props.options"
      :key="String(option.value)"
      ref="radioRefs"
      v-model="model"
      :name="props.name"
      :option="option"
      :disabled="props.disabled"
      :direction="props.optionDirection"
      :has-error="hasErrors"
      @focus="onFocus"
      @blur="onBlur"
    />
    <template v-if="dsFormField.errorMessages.value.length">
      <DSFormFieldError
        v-for="(errorMessage, index) in dsFormField.errorMessages.value"
        :key="`${props.name}-error-${index}`"
        :error-message="errorMessage"
      />
    </template>
  </DSFieldset>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { DSRadioGroupEmits } from "../types/emits";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import DSRadioButton from "@/src/design_system/form_elements/radio/shared_modules/radio_button/components/DSRadioButton.vue";
import { DS_DIRECTION } from "@/src/design_system/shared/types/direction";
import DSFieldset from "../../../fieldset/components/DSFieldset.vue";
import { DSRadioGroupProps } from "@/src/design_system/form_elements/radio/radio_group/types/props";
import { DSRadioModelValue } from "@/src/design_system/form_elements/radio/shared/types/props";
import DSFormFieldError from "@/src/design_system/form_elements/form_field_error/components/DSFormFieldError.vue";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { crossesFocusBoundary } from "@/src/design_system/form_elements/shared/utils/focusBoundary";

const props = withDefaults(defineProps<DSRadioGroupProps>(), {
  hiddenLabel: false,
  label: undefined,
  tooltip: undefined,
  disabled: false,
  direction: DS_DIRECTION.Vertically,
  optionDirection: DS_DIRECTION.Horizontally,
  required: false,
  hasError: false,
  errorMessages: undefined,
  rules: undefined,
});

const model = defineModel<DSRadioModelValue>();
const emit = defineEmits<DSRadioGroupEmits>();
const fieldsetRef = ref<InstanceType<typeof DSFieldset>>();
const rootElement = () => fieldsetRef.value?.$el as HTMLElement | undefined;
const radioRefs = ref<InstanceType<typeof DSRadioButton>[]>([]);
const focusTarget = computed(() => {
  if (props.disabled) return;
  const option = props.options.find(option => option.value === model.value) ?? props.options[0];
  return radioRefs.value.find(radio => radio.$props.option.value === option?.value);
});
const { focus } = useDSInput(focusTarget);
const blur = () => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && rootElement()?.contains(activeElement)) activeElement.blur();
};
const onFocus = (event: FocusEvent) => {
  if (!crossesFocusBoundary(event, rootElement())) return;
  emit("focus", event);
};
const onBlur = (event: FocusEvent) => {
  if (!crossesFocusBoundary(event, rootElement())) return;
  emit("blur", event);
};

defineExpose({ focus, blur });

const dsFormField = useDSFormField<DSRadioModelValue>({
  name: computed(() => props.name),
  errorMessages: computed(() => props.errorMessages),
  value: computed(() => model.value),
  rules: computed(() => props.rules),
});

const hasErrors = computed(() => props.hasError || !!dsFormField.errorMessages.value.length);
</script>
